import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';

// Layout components
import Layout from './components/layout/Layout';
import PrivateRoute from './components/common/PrivateRoute';
import ArtistRoute from './components/common/ArtistRoute';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import EmailVerificationPage from './pages/auth/EmailVerificationPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import UploadReleasePage from './pages/tracks/UploadReleasePage';
import ReleasePage from './pages/releases/ReleasePage';
import PromoTrackPage from './pages/promo/PromoTrackPage';
import ProfilePage from './pages/profile/ProfilePage';
import NotFoundPage from './pages/NotFoundPage';
import PromoPage from './pages/promo/PromoTrackPage';
import EditReleasePage from './pages/releases/EditReleasePage';

// Curator pages
import CuratorLibraryPage from './pages/curator/CuratorLibraryPage';
import PlayLogPage from './pages/curator/PlayLogPage';
import ArtistAnalyticsPage from './pages/curator/ArtistAnalyticsPage';

// Email pages
import EmailListsPage from './pages/email/EmailListsPage';

// Legal pages
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import TakedownPage from './pages/legal/TakedownPage';

// Track redirect component
const TrackRedirect = () => {
  const { trackId } = useParams();
  return <Navigate to={`/release/${trackId}`} replace />;
};

// Create a theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#a187f2', // Purple accent color
      dark: '#8a6fe0', // Slightly darker purple for hover states
      light: '#b89df5', // Lighter purple for backgrounds
      contrastText: '#FFFFFF', // White text on purple backgrounds
    },
    secondary: {
      main: '#e4e4e4', // Bright grey as secondary
      dark: '#d0d0d0', // Darker grey for hover
      contrastText: '#1f1f21',
    },
    background: {
      default: '#666666', // Steel grey as main background
      paper: '#e4e4e4', // Bright grey for cards/papers
    },
    text: {
      primary: '#1f1f21', // Dark grey for primary text
      secondary: '#666666', // Steel grey for secondary text
    },
    action: {
      active: '#a187f2',
      hover: 'rgba(161, 135, 242, 0.08)',
      selected: 'rgba(161, 135, 242, 0.16)',
      disabled: 'rgba(31, 31, 33, 0.3)',
      disabledBackground: 'rgba(31, 31, 33, 0.12)',
    },
    divider: 'rgba(31, 31, 33, 0.12)',
  },
  typography: {
    fontFamily: "'Martian Mono', 'Martian Mono Condensed', monospace",
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.3),0px 1px 1px 0px rgba(0,0,0,0.24),0px 1px 3px 0px rgba(0,0,0,0.22)',
    // ...existing shadows...
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 4,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.2)',
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#8a6fe0',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1f1f21', // Dark grey for header
          backgroundImage: 'none',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
          color: '#e4e4e4', // Bright grey for text
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: '#a187f2',
          color: '#FFFFFF',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#1f1f21',
          '&.Mui-focused': {
            color: '#1f1f21',
          },
          '&.Mui-disabled': {
            color: '#666666',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#1f1f21',
          '&.Mui-focused': {
            color: '#1f1f21',
          },
          '&.Mui-disabled': {
            color: '#666666',
          },
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/" element={<Layout />}>
              {/* Public routes */}
              <Route index element={<HomePage />} />
              <Route path="login" element={<LoginPage />} />
              <Route path="register" element={<RegisterPage />} />
              <Route path="verify-email" element={<EmailVerificationPage />} />
              <Route path="promo/release/:promoCode" element={<PromoPage />} />
              <Route path="promo/track/:trackId" element={<PromoTrackPage />} />
              
              {/* Legal pages */}
              <Route path="terms" element={<TermsPage />} />
              <Route path="privacy" element={<PrivacyPage />} />
              <Route path="takedown" element={<TakedownPage />} />
              
              {/* Artist-only routes */}
              <Route element={<ArtistRoute />}>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="upload/release" element={<UploadReleasePage />} />
                <Route path="artist/analytics" element={<ArtistAnalyticsPage />} />
                <Route path="email-lists" element={<EmailListsPage />} />
              </Route>
              
              {/* Protected routes for all logged-in users */}
              <Route element={<PrivateRoute />}>
                <Route path="track/:trackId" element={<TrackRedirect />} />
                <Route path="release/:releaseId" element={<ReleasePage />} />
                <Route path="releases/:releaseId/edit" element={<EditReleasePage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="curator/library" element={<CuratorLibraryPage />} />
                <Route path="curator/play-logs" element={<PlayLogPage />} />
              </Route>
              
              {/* 404 route */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
